import {useFundamentalsLanguage} from '@hconnect/common/hooks'
import {BackendFile, Button, ControlledDragAndDropFiles, Modal} from '@hm/fundamental'
import {yupResolver} from '@hookform/resolvers/yup'
import {useMemo} from 'react'
import {useForm, Controller} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import * as Yup from 'yup'

import {FEATURE_NAMESPACE} from '../../consts'
import {useAddRefractoryDocuments} from '../../hooks/mutations'
import {useRefraModal} from '../../providers'
import {checkFileFormat, checkFileSize} from '../../utils'

interface RefraCatalogAddDocumentsModalProps {
  refractoryId: number
}

interface FormData {
  files: (File | BackendFile)[]
}

export const RefraCatalogAddDocumentsModal: React.FC<RefraCatalogAddDocumentsModalProps> = ({
  refractoryId
}) => {
  const language = useFundamentalsLanguage()

  const {t} = useTranslation(FEATURE_NAMESPACE)

  const {closeModal} = useRefraModal()

  const {mutate: addDocuments, isPending: isAddingDocuments} = useAddRefractoryDocuments()

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        files: Yup.array()
          .min(1, t('error.fileRequired'))
          .required(t('error.fileRequired'))
          .test(
            'fileSize',
            t('error.fileTooLarge'),
            (files) => !files || files.every((file) => checkFileSize(file as File))
          )
          .test(
            'fileFormat',
            t('error.fileFormat'),
            (files) => !files || files.every((file) => checkFileFormat(file as File))
          )
      }),
    [t]
  )

  const {
    handleSubmit,
    control,
    reset,
    formState: {errors, touchedFields, isValid}
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      files: []
    }
  })

  const onSubmit = (data: FormData) => {
    addDocuments({refractoryId, documents: data.files as File[]}, {onSuccess: () => reset()})
  }

  return (
    <Modal open onClose={closeModal} data-cy="refra-catalog-add-documents-modal">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Controller
          name="files"
          control={control}
          render={({field}) => (
            <ControlledDragAndDropFiles
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              error={errors.files?.message}
              touched={!touchedFields.files}
              language={language}
              hint={t('warning.fileSize')}
              disabled={isAddingDocuments}
              data-cy="refra-catalog-add-documents-drag-and-drop-input"
            />
          )}
        />
        <div className="flex justify-end mt-3 mr-3">
          <Button
            type="submit"
            disabled={isAddingDocuments || !isValid}
            data-cy="refra-catalog-add-documents-button"
          >
            {t('common.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
